import React, { useState, useEffect } from 'react';
import { useToast } from '../../contexts/ToastContext';
import { investmentSheetPhService } from '../../services/investmentSheetService';
import { investorService } from '../../services/userService';
import { phProductService } from '../../services/productService';
import { phPhaseService } from '../../services/phaseService';
import { InvestmentSheetPh } from '../../types/InvestmentSheet';
import { Investor } from '../../types/User';
import { PhProduct } from '../../types/InvestmentProduct';
import { PhPhase } from '../../types/Phase';
import InvestorAutocomplete from '../common/InvestorAutocomplete';
import PdfUpload from '../common/PdfUpload';
import DateInput from '../common/DateInput';
import AmortizacionesList from '../amortizaciones/AmortizacionesList';
import { formatCurrency, parseCurrencyInput, formatCurrencyInput } from '../../utils/format';
import '../users/UserForm.css';

interface InvestmentPhFormProps {
  sheet: InvestmentSheetPh | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const InvestmentPhForm: React.FC<InvestmentPhFormProps> = ({ sheet, onSuccess, onCancel }) => {
  const { showToast } = useToast();
  const [formData, setFormData] = useState<Partial<InvestmentSheetPh>>({
    investorId: '',
    productId: '',
    phaseId: '',
    importe: 0,
    origenFondos: '',
    titularCuentaDevolucion: '',
    ibanDevolucion: '',
    porcentajeIntFase: undefined,
    porcentajeIntFidelizacion: undefined,
    estado: '',
    estadoKyc: '',
    capitalDevuelto: 0,
  });

  const [investors, setInvestors] = useState<Investor[]>([]);
  const [products, setProducts] = useState<PhProduct[]>([]);
  const [phases, setPhases] = useState<PhPhase[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [importeInput, setImporteInput] = useState('');

  useEffect(() => {
    loadInvestors();
    loadProducts();
    if (sheet) {
      setFormData(sheet);
      setImporteInput(sheet.importe ? formatCurrencyInput(sheet.importe) : '');
      if (sheet.productId) {
        loadPhases(sheet.productId);
      }
    }
  }, [sheet]);

  const loadInvestors = async () => {
    try {
      const response = await investorService.getAll();
      setInvestors(response.data);
    } catch (err) {
      console.error('Error loading investors:', err);
    }
  };

  const loadProducts = async () => {
    try {
      const response = await phProductService.getAll();
      setProducts(response.data);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const loadPhases = async (productId: string) => {
    try {
      const response = await phPhaseService.getAll(productId);
      setPhases(response.data);
    } catch (err) {
      console.error('Error loading phases:', err);
    }
  };

  const handleInvestorChange = (investorId: string) => {
    // Cuando se selecciona un inversor, autocompletar datos de devolución
    const selectedInvestor = investors.find(inv => inv.id === investorId);
    setFormData((prev) => ({
      ...prev,
      investorId: investorId,
      // Autocompletar con datos del inversor, pero permiten ser editados
      titularCuentaDevolucion: selectedInvestor?.titularCuentaPrincipal || prev.titularCuentaDevolucion || '',
      ibanDevolucion: selectedInvestor?.ibanPrincipal || prev.ibanDevolucion || '',
    }));
  };

  const handleImporteChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value;
    setImporteInput(raw);
    setFormData((prev) => ({ ...prev, importe: parseCurrencyInput(raw) }));
  };

  const handleImporteBlur = () => {
    setImporteInput(formData.importe != null ? formatCurrencyInput(formData.importe) : '');
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;

    if (name === 'productId') {
      setFormData((prev) => ({
        ...prev,
        productId: value,
        phaseId: '',
      }));
      if (value) {
        loadPhases(value);
      } else {
        setPhases([]);
      }
    } else if (name === 'phaseId') {
      // Cuando se selecciona una fase, autocompletar intereses desde la fase
      const selectedPhase = phases.find(phase => phase.id === value);
      setFormData((prev) => ({
        ...prev,
        phaseId: value,
        // %_INT_FASE se trae de la fase (interesTotal), NO EDITABLE
        porcentajeIntFase: selectedPhase?.interesTotal || prev.porcentajeIntFase,
        // %_INT_FIDELIZACIÓN se trae de la fase (intFidelizacion), EDITABLE
        porcentajeIntFidelizacion: selectedPhase?.intFidelizacion || prev.porcentajeIntFidelizacion,
      }));
    } else if (name === 'porcentajeIntFidelizacion') {
      // porcentajeIntFase no se incluye aquí porque no es editable
      setFormData((prev) => ({
        ...prev,
        [name]: value ? parseFloat(value) : undefined,
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      if (sheet?.id) {
        await investmentSheetPhService.update(sheet.id, formData);
        showToast('Inversión PH actualizada correctamente');
      } else {
        await investmentSheetPhService.create(formData);
        showToast('Inversión PH creada correctamente');
      }
      onSuccess();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error al guardar la ficha de inversión');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="user-form">
      <h3>{sheet ? 'Editar Inversión PH' : 'Crear Inversión PH'}</h3>
      <p className="form-subtitle">Renta Fija - Interés fijo con pagos periódicos</p>
      {error && <div className="error-message">{error}</div>}
      <form onSubmit={handleSubmit}>
        <h4>Identificación</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="investorId">Inversor *</label>
            <InvestorAutocomplete
              investors={investors}
              value={formData.investorId || ''}
              onChange={handleInvestorChange}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="productId">Producto *</label>
            <select
              id="productId"
              name="productId"
              value={formData.productId}
              onChange={handleChange}
              required
            >
              <option value="">Seleccionar...</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {product.nombreProducto}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="phaseId">Fase *</label>
            <select
              id="phaseId"
              name="phaseId"
              value={formData.phaseId}
              onChange={handleChange}
              required
              disabled={!formData.productId}
            >
              <option value="">Seleccionar...</option>
              {phases.map((phase) => (
                <option key={phase.id} value={phase.id}>
                  {phase.nombreFase}
                </option>
              ))}
            </select>
          </div>
        </div>

        <h4>Económico</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="importe">Importe *</label>
            <input
              type="text"
              inputMode="decimal"
              id="importe"
              name="importe"
              value={importeInput}
              onChange={handleImporteChange}
              onBlur={handleImporteBlur}
              placeholder="0,00"
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="porcentajeIntFase">% Interés Fase</label>
            <input
              type="number"
              id="porcentajeIntFase"
              name="porcentajeIntFase"
              value={formData.porcentajeIntFase || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
              disabled
              title="Este valor se obtiene automáticamente de la fase seleccionada"
            />
          </div>
          <div className="form-group">
            <label htmlFor="porcentajeIntFidelizacion">% Interés Fidelización</label>
            <input
              type="number"
              id="porcentajeIntFidelizacion"
              name="porcentajeIntFidelizacion"
              value={formData.porcentajeIntFidelizacion || ''}
              onChange={handleChange}
              step="0.01"
              min="0"
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="origenFondos">Origen de Fondos</label>
            <input
              type="text"
              id="origenFondos"
              name="origenFondos"
              value={formData.origenFondos || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="titularCuentaDevolucion">Titular Cuenta Devolución</label>
            <input
              type="text"
              id="titularCuentaDevolucion"
              name="titularCuentaDevolucion"
              value={formData.titularCuentaDevolucion || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group span-2">
            <label htmlFor="ibanDevolucion">IBAN Devolución</label>
            <input
              type="text"
              id="ibanDevolucion"
              name="ibanDevolucion"
              value={formData.ibanDevolucion || ''}
              onChange={handleChange}
              maxLength={34}
            />
          </div>
        </div>

        <h4>Estado y Cumplimiento</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="estado">Estado</label>
            <input
              id="estado"
              name="estado"
              value={formData.estado || 'Prospecto'}
              disabled
            />
          </div>
          <div className="form-group">
            <label htmlFor="estadoKyc">Estado KYC</label>
            <select
              id="estadoKyc"
              name="estadoKyc"
              value={formData.estadoKyc || ''}
              onChange={handleChange}
            >
              <option value="">Seleccionar...</option>
              <option value="Pendiente">Pendiente</option>
              <option value="En revisión">En revisión</option>
              <option value="Aprobado">Aprobado</option>
              <option value="Rechazado">Rechazado</option>
            </select>
          </div>
        </div>

        <h4>Flujo de Gestión</h4>
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="solicitaContrato">Solicita Contrato</label>
            <DateInput
              id="solicitaContrato"
              name="solicitaContrato"
              value={formData.solicitaContrato || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaContrato">Fecha Contrato</label>
            <DateInput
              id="fechaContrato"
              name="fechaContrato"
              value={formData.fechaContrato || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaFirmaCliente">Firma Cliente</label>
            <DateInput
              id="fechaFirmaCliente"
              name="fechaFirmaCliente"
              value={formData.fechaFirmaCliente || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaFirmaClub">Firma Club</label>
            <DateInput
              id="fechaFirmaClub"
              name="fechaFirmaClub"
              value={formData.fechaFirmaClub || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaTransRecibida">Transferencia Recibida</label>
            <DateInput
              id="fechaTransRecibida"
              name="fechaTransRecibida"
              value={formData.fechaTransRecibida || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaDevCapital">Devolución Capital</label>
            <DateInput
              id="fechaDevCapital"
              name="fechaDevCapital"
              value={formData.fechaDevCapital || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="fechaLiquidacion">Fecha Liquidación</label>
            <DateInput
              id="fechaLiquidacion"
              name="fechaLiquidacion"
              value={formData.fechaLiquidacion || ''}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label htmlFor="fechaRechazado">Fecha Rechazado</label>
            <DateInput
              id="fechaRechazado"
              name="fechaRechazado"
              value={formData.fechaRechazado || ''}
              onChange={handleChange}
            />
          </div>
        </div>

        {sheet?.id && (
          <>
            <h4>Documentos</h4>
            <div className="form-row">
              <div className="form-group">
                <PdfUpload
                  sheetId={sheet.id}
                  docType="contrato"
                  currentFile={formData.contratoPdf}
                  label="Contrato"
                  onUploadSuccess={(key) => setFormData((prev) => ({ ...prev, contratoPdf: key }))}
                  onDeleteSuccess={() => setFormData((prev) => ({ ...prev, contratoPdf: undefined }))}
                />
              </div>
              <div className="form-group">
                <PdfUpload
                  sheetId={sheet.id}
                  docType="transferencia"
                  currentFile={formData.transferenciaPdf}
                  label="Transferencia"
                  onUploadSuccess={(key) => setFormData((prev) => ({ ...prev, transferenciaPdf: key }))}
                  onDeleteSuccess={() => setFormData((prev) => ({ ...prev, transferenciaPdf: undefined }))}
                />
              </div>
            </div>
          </>
        )}

        {sheet && (
          <>
            <h4>Métricas Calculadas (Solo lectura)</h4>
            <div className="form-row">
              <div className="form-group">
                <label>Int. Fidelización</label>
                <input
                  type="text"
                  value={formatCurrency(formData.intFidelizacion)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Intereses Acumulados</label>
                <input
                  type="text"
                  value={formatCurrency(formData.interesesAcumulados)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Total a Liquidar</label>
                <input
                  type="text"
                  value={formatCurrency(formData.totalALiquidar)}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Intereses Pagados</label>
                <input
                  type="text"
                  value={formatCurrency(formData.interesesPagados)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Capital Devuelto</label>
                <input
                  type="text"
                  value={formatCurrency(formData.capitalDevuelto)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Liq. Final Pendiente</label>
                <input
                  type="text"
                  value={formatCurrency(formData.liqFinalPendiente)}
                  disabled
                />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Rto. Anual Inicial</label>
                <input
                  type="text"
                  value={formatPercent(formData.rtoAnualInicial)}
                  disabled
                />
              </div>
              <div className="form-group">
                <label>Rto. Anual Actual</label>
                <input
                  type="text"
                  value={formatPercent(formData.rtoAnualActual)}
                  disabled
                />
              </div>
            </div>
          </>
        )}

        {sheet?.id && <AmortizacionesList sheetId={sheet.id} hideWhenEmpty />}

        <div className="form-actions">
          <button type="button" onClick={onCancel} className="btn-secondary" disabled={loading}>
            Cancelar
          </button>
          <button type="submit" className="btn-primary" disabled={loading}>
            {loading ? 'Guardando...' : sheet ? 'Actualizar' : 'Crear'}
          </button>
        </div>
      </form>
    </div>
  );
};

const formatPercent = (value?: number) => {
  if (value === undefined || value === null) return '-';
  return `${value.toFixed(2)}%`;
};

export default InvestmentPhForm;
